import { useEffect, useState } from 'react';
import { ExternalLink, RefreshCw, Search } from 'lucide-react';
import { ApiError, fetchLeadChecker } from '../api';
import type { LeadOverview, LeadView } from '../types';
import { ErrorState } from './ErrorState';

const integer = new Intl.NumberFormat('ru-RU');
const date = new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
function when(value: string | null | undefined) { return value ? date.format(new Date(value)) : '—'; }
function money(amount: number | null, currency: string | null) {
  if (amount == null || !currency) return '—';
  try { return new Intl.NumberFormat('ru-RU', { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount / 100); }
  catch { return `${(amount / 100).toFixed(2)} ${currency}`; }
}
function safeUrl(value: string | null) {
  try { const url = new URL(value || ''); return ['http:', 'https:'].includes(url.protocol) ? url.href : null; }
  catch { return null; }
}
function resultLabel(value: string | null) {
  const labels: Record<string, string> = {
    http_404: 'Страница вернула 404',
    expired_ad_redirect: 'Площадка перенаправила на архив',
    price_changed: 'Новая цена подтверждена',
    available: 'Объявление доступно',
    unknown: 'Наличие не подтверждено',
    out_of_stock: 'Нет в наличии',
  };
  return value ? (labels[value] ?? value.replaceAll('_', ' ')) : '—';
}
function initialView(): LeadView {
  const view = new URLSearchParams(window.location.search).get('view');
  return view === 'changed' || view === 'available' ? view : 'gone';
}

export function LeadCheckerDashboard({ onUnauthorized }: { onUnauthorized: () => void }) {
  const [view, setView] = useState<LeadView>(initialView);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<LeadOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const query = new URLSearchParams();
    if (view !== 'gone') query.set('view', view);
    if (q.trim()) query.set('q', q.trim());
    if (page !== 1) query.set('page', String(page));
    window.history.replaceState(null, '', `/lead-checker${query.size ? `?${query}` : ''}`);
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setLoading(true);
      void fetchLeadChecker(view, q, page, controller.signal).then(result => {
        if (page > 1 && !result.items.length) setPage(1);
        else setData(result);
        setError('');
      }).catch(caught => {
        if (controller.signal.aborted) return;
        if (caught instanceof ApiError && caught.status === 401) onUnauthorized();
        else setError(caught instanceof Error ? caught.message : 'Неизвестная ошибка');
      }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, q ? 250 : 0);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [view, q, page, revision, onUnauthorized]);

  const choose = (next: LeadView) => { setView(next); setPage(1); setQ(''); };
  if (error) return <ErrorState message={error} onRetry={() => setRevision(value => value + 1)} />;
  return <>
    <div className="page-heading"><div><span className="eyebrow">Повторная проверка объявлений</span><h1>Lead Checker</h1><p>Показывает, что изменилось у уже найденных предложений.</p></div><button className="refresh-button" onClick={() => setRevision(value => value + 1)} disabled={loading}><RefreshCw size={16} />Обновить</button></div>
    {!data ? <div className="loading-table" aria-label="Загрузка Lead Checker" /> : <>
      <div className="lead-note">Последняя попытка проверки: <strong>{when(data.summary.lastAttemptedAt)}</strong>. {data.publicationEnabled ? 'Результаты применяются к общему каталогу.' : 'Исключение исчезнувших предложений из общего каталога пока не включено.'}</div>
      <div className="lead-metrics">
        <div><small>Под наблюдением</small><strong>{integer.format(data.summary.total)}</strong></div>
        <div><small>Доступность подтверждена</small><strong>{integer.format(data.summary.available)}</strong></div>
        <div className="lead-alert"><small>Страница исчезла</small><strong>{integer.format(data.summary.gone)}</strong></div>
        <div><small>Цена изменилась</small><strong>{integer.format(data.summary.priceChanged)}</strong></div>
      </div>
      <div className="lead-secondary">Остаток не подтверждён: {integer.format(data.summary.stockUnknown)} · Нет товара в наличии: {integer.format(data.summary.outOfStock)} · Ответ не подтверждён: {integer.format(data.summary.unverified)}</div>
      <section className="lead-panel">
        <div className="lead-head"><div><h2>Результаты проверок</h2><p>Цены пересчитаны в евро по курсу Exchanger. Поиск работает внутри выбранного списка.</p></div><label className="lead-search"><Search size={16} /><input value={q} onChange={event => { setQ(event.target.value); setPage(1); }} placeholder="Название, площадка или ID" maxLength={100} /></label></div>
        <div className="mv-tabs" role="tablist" aria-label="Результаты Lead Checker"><button className={view === 'gone' ? 'active' : ''} role="tab" aria-selected={view === 'gone'} onClick={() => choose('gone')}>Исчезли ({data.summary.gone})</button><button className={view === 'changed' ? 'active' : ''} role="tab" aria-selected={view === 'changed'} onClick={() => choose('changed')}>Цена изменилась ({data.summary.priceChanged})</button><button className={view === 'available' ? 'active' : ''} role="tab" aria-selected={view === 'available'} onClick={() => choose('available')}>Доступны · примеры</button></div>
        <div className="lead-table-scroll"><table className="lead-table"><thead><tr><th>Объявление</th><th>Результат</th><th>Последняя цена, €</th><th>Проверено</th></tr></thead><tbody>{data.items.map(item => {
          const url = safeUrl(item.url);
          const result = view === 'gone' ? 'Страница не найдена' : view === 'changed' ? 'Цена изменилась' : 'Доступно';
          return <tr key={item.key}><td><strong>{item.title || `ID ${item.listingId}`}</strong><small>{item.source} · ID {item.listingId}</small>{url && <a href={url} target="_blank" rel="noopener noreferrer">Исходная страница <ExternalLink size={12} /></a>}</td><td><span className={`lead-status ${view}`}>{result}</span><small>{resultLabel(item.result)}</small></td><td>{view === 'changed' && item.previousAmountMinor != null && <small>было {money(item.previousAmountMinor, item.currency)}</small>}<strong>{money(item.amountMinor, item.currency)}</strong></td><td>{when(item.checkedAt)}{item.confirmedAt && <small>{view === 'gone' ? 'последний раз доступно' : 'подтверждено'} {when(item.confirmedAt)}</small>}</td></tr>;
        })}</tbody></table>{!data.items.length && <div className="mv-empty">По этому запросу объявлений нет.</div>}</div>
        <div className="mv-footer"><span>{data.total ? `${(page - 1) * data.pageSize + 1}–${Math.min(page * data.pageSize, data.total)} из ${data.total}` : '0 объявлений'}</span><div><button disabled={page <= 1} onClick={() => setPage(page - 1)}>Назад</button><button disabled={page * data.pageSize >= data.total} onClick={() => setPage(page + 1)}>Далее</button></div></div>
      </section>
    </>}
  </>;
}
